/* eslint-disable dot-notation */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { color, space, typography } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { CircleFlag } from 'react-circle-flags';
import base64 from 'base-64';
import { useSelector, useDispatch } from 'react-redux';
import { getUnitsRequest } from 'redux-api/action/units';
import { changeOthersFilter } from 'redux-api/action/filters';
import { selectUnitsListArray } from 'redux-api/reselect/units';
import selectProfile from 'redux-api/reselect/profile';
import colors from 'theme/charts/colors';
import Table from 'theme/table/analytics';
import BtnLink from 'theme/buttons/btn-link';
import Filter from 'theme/icons/filter';
import ItemTypeCard from 'theme/item-type-card';
import translations from 'translations';
import changeCoverUrl from 'lib/change-cover-url';

const Label = styled.div`
  ${color}
  ${space}

  display: flex;
  flex-direction: row;
  width: fit-content;
  border-radius: 4px;

  & > img {
    padding-left: ${themeGet('space.xs')}px;
  }
`;

const Text = styled.p`
  ${typography}
  ${color}
  ${space}

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: ${({ textTransform }) => textTransform || 'none'};
`;

const StyledCircleFlag = styled(CircleFlag)`
  width: 22px;
  height: 22px;
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: ${themeGet('colors.primary_400')};

  &:hover {
    background: ${themeGet('colors.primary_500')};
  }

  &:focus {
    background: ${themeGet('colors.primary_600')};
  }
`;

function TransationViewTablePreview({
  onButtonClick,
  buttonLabel,
  pagination,
  dataType,
  dateFrom,
  dateTo,
  dateRange,
  filters,
  terms,
  reloadChecked,
  size,
}) {
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [orderType, setOrderType] = useState('date_order');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [queriedKey, setQueriedKey] = useState(null);

  const dispatch = useDispatch();

  const { isWaiting } = useSelector((state) => state.units);
  const { units, total } = useSelector((state) => selectUnitsListArray(state, queriedKey));
  const { currency, languageCode } = useSelector((state) => state.config);

  const {
    isAdminAnalytics,
    payload: { analytics_type, company },
  } = useSelector(selectProfile);

  const handleAddIsbnFilter = useCallback(
    (isbn) => {
      if (isbn) {
        dispatch(
          changeOthersFilter({
            [JSON.stringify(`isbn-${isbn}`)]: {
              type: 'isbn',
              label: isbn,
              value: `isbn=${isbn}`,
              color: 'lime_300',
            },
          }),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!dataType || !dateTo || !dateFrom || !orderType || !orderDirection) return;

    const query = `offset=${offset}&size=${
      size || pageSize
    }&sort=${orderType}&dir=${orderDirection}&currency=${currency}&dataType=${dataType}&dateTo=${dateTo}&dateFrom=${dateFrom}&dateRange=${dateRange}&filters=${filters}&terms=${terms}&version=7&enterprise=${
      analytics_type || 'standard'
    }&isAdmin=${isAdminAnalytics}`;
    const key = base64.encode(query);

    setQueriedKey(key);

    dispatch(
      getUnitsRequest({
        key,
        query,
        currency,
        isAdmin: isAdminAnalytics,
      }),
    );
  }, [
    dispatch,
    currency,
    offset,
    pageSize,
    size,
    dateTo,
    dateFrom,
    dateRange,
    dataType,
    filters,
    terms,
    orderType,
    orderDirection,
    reloadChecked,
    analytics_type,
    isAdminAnalytics,
  ]);

  const columns = useMemo(
    () => [
      {
        title: translations[languageCode].Cover,
        width: 110,
        dataIndex: 'cover',
        key: 'cover',
        fixed: 'left',
        render: (cover) => (
          <img
            src={cover ? changeCoverUrl(cover) : '/images/book.jpg'}
            alt=""
            className="table-image"
          />
        ),
      },
      {
        title: translations[languageCode]['Title'],
        width: 290,
        dataIndex: 'title',
        sorter: true,
        key: 'title',
        fixed: 'left',
        ellipsis: {
          showTitle: true,
        },
        render: (text, record) => (
          <div>
            <Text
              fontSize="display"
              lineHeight="display"
              fontWeight="700"
              color="grey_600"
              textAlign="left"
            >
              {text}
            </Text>
            <Label alignItems="center">
              {record.item_id ? (
                <StyledLink
                  href={`${
                    company?.code === 'SL_DE'
                      ? process.env.UPLOAD_CONTENT_URI_DE
                      : process.env.UPLOAD_CONTENT_URI
                  }/publish_items/${record.item_id}`}
                >
                  <Text
                    fontSize="display"
                    lineHeight="display"
                    fontWeight="normal"
                    textAlign="left"
                  >
                    {record.isbn}
                  </Text>
                </StyledLink>
              ) : (
                <Text
                  fontSize="display"
                  lineHeight="display"
                  fontWeight="normal"
                  color="grey_600"
                  textAlign="left"
                >
                  {record.isbn}
                </Text>
              )}
              <BtnLink
                icon={<Filter />}
                onClick={() => handleAddIsbnFilter(record.isbn)}
                style={{ height: '30px', width: '36px', minWidth: '36px' }}
                title={translations[languageCode].Filter}
              />
            </Label>
          </div>
        ),
      },
      {
        title: translations[languageCode].Type,
        dataIndex: 'type',
        key: 'item_type',
        sorter: true,
        width: 120,
        render: (text) => <ItemTypeCard type={text} />,
      },
      {
        title: translations[languageCode].Price,
        dataIndex: 'price',
        key: `price_${currency}`,
        sorter: true,
        width: 150,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => text || '-',
      },
      {
        title:
          dataType === 'usage_data'
            ? translations[languageCode].hours
            : translations[languageCode].Revenues,
        dataIndex: dataType === 'usage_data' ? 'duration' : 'earning',
        key: dataType === 'usage_data' ? 'duration' : `revenues_${currency}.supplier`,
        sorter: true,
        width: 150,
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title:
          dataType === 'usage_data'
            ? translations[languageCode].users
            : translations[languageCode].Quantity,
        dataIndex: isAdminAnalytics ? 'quantityAdmin' : 'quantity',
        key: 'quantity',
        sorter: true,
        width: 100,
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title: translations[languageCode].DocumentType,
        dataIndex: 'documentSourceType',
        key: 'documentSourceType',
        sorter: false,
        width: 150,
        render: (text) => (
          <Label
            px="xs"
            py="xxs"
            color={text && 'white'}
            bg={
              text === 'invoicing'
                ? colors.business_revenues[1]
                : text === 'repartition_charge'
                ? colors.business_revenues[2]
                : text === 'repartition_credit'
                ? colors.business_revenues[3]
                : text === 'temporary'
                ? colors.business_revenues[4]
                : 'transparent'
            }
          >
            {text === 'invoicing'
              ? translations[languageCode].Invoicing
              : text === 'repartition_charge'
              ? translations[languageCode]['Repartition charge']
              : text === 'repartition_credit'
              ? translations[languageCode]['Repartition credit']
              : text === 'temporary'
              ? translations[languageCode]['Temporary']
              : text || '-'}
          </Label>
        ),
      },
      {
        title: translations[languageCode].Orderdate,
        dataIndex: 'orderDate',
        key: 'date_order',
        sorter: true,
        width: 150,
      },
      {
        title: translations[languageCode].Country,
        dataIndex: 'countryObj',
        key: `country.${languageCode}`,
        sorter: true,
        width: 200,
        ellipsis: {
          showTitle: true,
        },
        render: (country) => (
          <Label>
            {country.label}
            <StyledCircleFlag countryCode={country.code} />
          </Label>
        ),
      },
      {
        title: translations[languageCode].Language,
        dataIndex: 'language',
        key: `language.${languageCode}`,
        sorter: true,
        width: 150,
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title: translations[languageCode].OrderType,
        dataIndex: 'orderType',
        key: 'order_type',
        sorter: true,
        width: 150,
        render: (text) => (
          <Label
            px="xs"
            py="xxs"
            color="white"
            bg={
              text === 'rent'
                ? colors.business_revenues[2]
                : text === 'buy'
                ? colors.business_revenues[0]
                : text === 'refund'
                ? colors.business_revenues[0]
                : text === 'lending'
                ? colors.business_revenues[4]
                : text === 'repartition_charge'
                ? colors.business_revenues[5]
                : colors.business_revenues[1]
            }
          >
            {text === 'rent'
              ? translations[languageCode].libraries
              : text === 'buy'
              ? 'Retail'
              : text === 'adjustment'
              ? translations[languageCode].adjustment
              : text === 'refund'
              ? translations[languageCode].refund
              : translations[languageCode][text]}
          </Label>
        ),
      },
      {
        title: translations[languageCode].DataType,
        dataIndex: 'dataType',
        key: 'data_type',
        sorter: true,
        width: 140,
        render: (text) => (
          <Label
            px="xs"
            py="xxs"
            color="white"
            bg={
              text === 'temporary_data'
                ? colors.business_revenues[5]
                : text === 'real_data'
                ? colors.business_revenues[4]
                : text === 'usage_data'
                ? colors.business_revenues[3]
                : colors.business_revenues[2]
            }
          >
            {text === 'temporary_data'
              ? translations[languageCode].Temporary
              : text === 'real_data'
              ? translations[languageCode].realdata
              : text === 'usage_data'
              ? translations[languageCode].usagedata
              : text}
          </Label>
        ),
      },
      {
        title: translations[languageCode].Authors,
        dataIndex: 'authors',
        key: 'authors',
        sorter: true,
        width: 150,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => <span>{text || '-'}</span>,
      },
      {
        title: translations[languageCode].Genre,
        dataIndex: 'genre',
        key: `main_category.${languageCode}`,
        sorter: true,
        width: 200,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => <span>{text || '-'}</span>,
      },
      {
        title: translations[languageCode].Publisher,
        dataIndex: 'publisher',
        key: 'publisher',
        sorter: true,
        width: 150,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => <span>{text || '-'}</span>,
      },
      {
        title: translations[languageCode].StorePartner,
        dataIndex: 'storePartner',
        key: 'store_partner_name',
        sorter: true,
        width: 150,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => <span>{text || '-'}</span>,
      },
      {
        title: translations[languageCode].Store,
        dataIndex: 'store',
        key: 'store_name',
        width: 200,
        sorter: true,
        ellipsis: {
          showTitle: true,
        },
        fixed: 'right',
      },
    ],
    [languageCode, currency, dataType, handleAddIsbnFilter, company],
  );

  const handleChange = useCallback(({ pagination: { current, pageSize: ps }, sorter }) => {
    const { order, columnKey } = sorter;

    if (current && ps) {
      setOffset((current - 1) * ps);
    }

    if (order) setOrderDirection(order === 'descend' ? 'desc' : 'asc');
    if (columnKey) setOrderType(columnKey);
  }, []);

  const handleClickTableBtn = useCallback(() => {
    if (onButtonClick) return onButtonClick();

    return null;
  }, [onButtonClick]);

  const handleChangeSize = useCallback((current, s) => {
    setPageSize(s);
  }, []);

  return (
    <Table
      title={translations[languageCode]['transaction view']}
      emptyLabel={translations[languageCode]['no available']}
      buttonLabel={buttonLabel}
      onButtonClick={handleClickTableBtn}
      totalLabel={translations[languageCode]['total product']}
      columns={columns}
      dataSource={units}
      pagination={
        pagination
          ? {
              ...pagination,
              pageSize,
              total,
              onShowSizeChange: handleChangeSize,
            }
          : false
      }
      total={total || 0}
      onChangeTable={handleChange}
      loading={isWaiting}
    />
  );
}

export default TransationViewTablePreview;
